import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { configApi } from '../../configApi';

export interface IRegistrationDocumentAttachment {
  id: string;
  name: string;
  size: number;
  created_at: string;
  file: string;
}

export interface IRegistrationDocument {
  type: string;
  label: string;
  required: boolean;
  conditional: boolean;
  condition: string | null;
  uploaded: boolean;
  attachment: IRegistrationDocumentAttachment | null;
}

export interface IRegistrationDocumentStatus {
  registration_id: string;
  is_finalized: boolean;
  can_upload: boolean;
  all_uploaded: boolean;
  missing_types: string[];
  documents: IRegistrationDocument[];
}

const statusKey = (registrationId: string) => ['registration_documents', registrationId];

export const useRegistrationDocumentStatus = (registrationId: string, enabled: boolean = true) =>
  useQuery({
    queryKey: statusKey(registrationId),
    queryFn: async () => {
      const response = await configApi.get<IRegistrationDocumentStatus>(
        `attachment/registration/${registrationId}/status/`,
      );
      return response.data;
    },
    enabled: enabled && Boolean(registrationId),
    refetchOnWindowFocus: false,
  });

export const useUploadRegistrationDocument = (registrationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      type,
      file,
      onProgress,
    }: {
      type: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('file', file);
      return (
        await configApi.post(`attachment/registration/${registrationId}/upload/`, formData, {
          onUploadProgress: ({ loaded, total }) => {
            if (total) onProgress?.(Math.round((loaded * 100) / total));
          },
        })
      ).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: statusKey(registrationId) }),
  });
};

export const useDeleteRegistrationDocument = (registrationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attachmentId: string) => (await configApi.delete(`attachment/delete/${attachmentId}/`)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: statusKey(registrationId) }),
  });
};
